import { useState } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CheckCircle, ChevronDown, ChevronUp, Shield, ArrowRight, Users, Layers, RefreshCw, Eye, Calendar, CreditCard, Gamepad2, Target, BookOpen } from "lucide-react";
import { Link } from "wouter";

const HANDS = [
  { name: "Royal Flush",      desc: "A-K-Q-J-10 stejné barvy",                    example: "A♠ K♠ Q♠ J♠ 10♠", rank: 1 },
  { name: "Straight Flush",   desc: "Pět po sobě jdoucích karet stejné barvy",     example: "6♥ 7♥ 8♥ 9♥ 10♥", rank: 2 },
  { name: "Four of a Kind",   desc: "Čtyři stejné karty",                          example: "9♣ 9♦ 9♥ 9♠ K♦",  rank: 3 },
  { name: "Full House",       desc: "Tři stejné + pár",                            example: "10♠ 10♥ 10♦ 7♠ 7♣", rank: 4 },
  { name: "Flush",            desc: "Pět karet stejné barvy",                      example: "2♠ 6♠ 9♠ J♠ K♠",  rank: 5 },
  { name: "Straight",         desc: "Pět po sobě jdoucích karet různých barev",    example: "4♦ 5♠ 6♥ 7♣ 8♠",  rank: 6 },
  { name: "Three of a Kind",  desc: "Tři stejné karty",                            example: "Q♥ Q♠ Q♦ 6♣ 2♠",  rank: 7 },
  { name: "Two Pair",         desc: "Dva páry",                                    example: "J♦ J♠ 8♣ 8♥ 5♠",  rank: 8 },
  { name: "One Pair",         desc: "Jeden pár",                                   example: "A♦ A♠ 7♣ 4♥ 3♦",  rank: 9 },
  { name: "High Card",        desc: "Nejvyšší karta, pokud žádná kombinace",       example: "A♠ 9♦ 6♣ 4♠ 2♥",  rank: 10 },
];

const STREETS = [
  {
    phase: "Pre-flop",
    step: 1,
    icon: "🂠",
    desc: "Každý hráč dostane dvě zakryté karty (hole cards). Probíhá první kolo sázek, začínající hráčem po Big Blindu.",
    board: ["?", "?"],
    hand: ["?", "?"],
  },
  {
    phase: "Flop",
    step: 2,
    icon: "🃏",
    desc: "Na stůl se vyloží tři společné karty lícem nahoru. Začíná druhé kolo sázek. Hráči poprvé vidí, jak silná může být jejich kombinace.",
    board: ["A♠", "K♥", "7♦"],
    hand: [],
  },
  {
    phase: "Turn",
    step: 3,
    icon: "🃏",
    desc: "Odhalí se čtvrtá společná karta (turn card). Třetí kolo sázek. Sázky bývají vyšší — hráči s dobrými kombinacemi přitlačují.",
    board: ["A♠", "K♥", "7♦", "J♣"],
    hand: [],
  },
  {
    phase: "River",
    step: 4,
    icon: "🃏",
    desc: "Poslední, pátá společná karta. Poslední kolo sázek — zde se rozhoduje. Bluffing nebo value betting na riveru patří k nejtěžším rozhodnutím v pokeru.",
    board: ["A♠", "K♥", "7♦", "J♣", "2♥"],
    hand: [],
  },
  {
    phase: "Showdown",
    step: 5,
    icon: "👁",
    desc: "Pokud zůstane více hráčů po posledním kole sázek, odhalí své karty. Vyhrává nejlepší pětikaretní kombinace, pot jde vítězi.",
    board: [],
    hand: [],
  },
];

const ACTIONS = [
  { name: "Check", desc: "Nepřidáváte sázku, ale zůstáváte ve hře. Možné pouze pokud nikdo před vámi nesázel.", color: "bg-gray-50 border-gray-200", badge: "bg-gray-200 text-gray-700" },
  { name: "Call", desc: "Dorovnáte sázku soupeře. Zůstáváte ve hře za cenu soupeřovy sázky.", color: "bg-blue-50 border-blue-200", badge: "bg-blue-200 text-blue-800" },
  { name: "Raise", desc: "Zvýšíte sázku. Ostatní hráči musí dorovnat váš raise, navýšit nebo složit.", color: "bg-green-50 border-green-200", badge: "bg-green-200 text-green-800" },
  { name: "Fold", desc: "Odložíte karty a vzdáváte toto kolo. Přijdete o vložené sázky, ale neriskujete víc.", color: "bg-red-50 border-red-200", badge: "bg-red-200 text-red-800" },
  { name: "All-in", desc: "Vsadíte všechny své žetony. Pokud soupeř call, hrajete o hlavní pot plus případný side pot.", color: "bg-amber-50 border-amber-200", badge: "bg-amber-200 text-amber-800" },
];

const VARIANTS = [
  {
    name: "Texas Hold'em",
    holeCards: 2,
    community: 5,
    rule: "Libovolná kombinace ze 2 vlastních + 5 společných karet.",
    difficulty: "Začátečník",
    diffColor: "text-green-600 bg-green-50 border-green-200",
    desc: "Nejhranější varianta na světě. Ideální pro začátečníky díky jednoduchým pravidlům a obrovské komunitě.",
  },
  {
    name: "Omaha",
    holeCards: 4,
    community: 5,
    rule: "Musíte použít přesně 2 ze 4 vlastních karet a přesně 3 ze společných.",
    difficulty: "Pokročilý",
    diffColor: "text-amber-600 bg-amber-50 border-amber-200",
    desc: "Více karet v ruce znamená silnější kombinace a větší poty. Větší variace než v Texas Hold'em.",
  },
  {
    name: "7-Card Stud",
    holeCards: 7,
    community: 0,
    rule: "Žádné společné karty. Každý hráč dostane 7 vlastních karet, skládá nejlepší 5.",
    difficulty: "Pokročilý",
    diffColor: "text-amber-600 bg-amber-50 border-amber-200",
    desc: "Klasická varianta bez blindů a společných karet. Vyžaduje silnou paměť a pozornost.",
  },
];

const TIPS = [
  { title: "Začněte u nízkých sázek", desc: "Abyste se naučili pravidla bez velkého rizika. Mikrostaky jsou ideální pro trénink — chyby vás nezruinují, ale naučíte se rychle." },
  { title: "Tight-aggressive styl", desc: "Hrajte disciplinovaně a vybírejte jen silné kombinace. Méně handů, ale každý hrajte agresivně — to je základ výdělečného pokeru." },
  { title: "Sledujte soupeře", desc: "Pozorujte jejich vzorce sázek, rychlost rozhodnutí a opakující se chování. Informace ze soupeřova chování jsou stejně cenné jako vaše karty." },
  { title: "Bankroll management", desc: "Nikdy neriskujte příliš velkou část svého kapitálu. Pravidlo: max. 5–10 % bankrollu na jeden turnaj nebo cash game stůl." },
];

const LEARNING_STEPS = [
  {
    n: "01",
    icon: BookOpen,
    title: "Pochopte základní pravidla",
    desc: "Každý hráč dostane dvě zakryté karty (hole cards). Na stůl se postupně rozdá pět společných karet. Cílem je sestavit nejsilnější pětikaretní kombinaci nebo donutit soupeře složit.",
    bullets: [
      "Hráči dostanou dvě zakryté karty (hole cards)",
      "Na stůl se postupně rozdá pět společných karet",
      "Hráči tvoří kombinace z pěti karet",
      "V každém kole mohou hráči vsadit, dorovnat, navýšit nebo složit",
      "Vyhrává nejlepší kombinace nebo poslední zbývající hráč",
    ],
  },
  {
    n: "02",
    icon: Layers,
    title: "Naučte se pokerové kombinace",
    desc: "Znalost pořadí kombinací je základ. Royal Flush je nejsilnější, High Card nejslabší. Pamatujte: barva (flush) poráží postupku (straight), ale prohrává s full housem.",
    bullets: [
      "Royal Flush > Straight Flush > Four of a Kind",
      "Full House > Flush > Straight",
      "Three of a Kind > Two Pair > One Pair > High Card",
    ],
  },
  {
    n: "03",
    icon: RefreshCw,
    title: "Zvládněte průběh hry",
    desc: "Hra v Texas Hold'em probíhá v pěti fázích: Pre-flop, Flop, Turn, River a Showdown. V každé fázi se odkrývají společné karty a probíhá kolo sázek.",
    bullets: [
      "Pre-flop: dvě zakryté karty, první kolo sázek",
      "Flop: tři společné karty, druhé kolo sázek",
      "Turn: čtvrtá společná karta, třetí kolo sázek",
      "River: pátá společná karta, poslední kolo sázek",
      "Showdown: odhalení karet, výherce bere pot",
    ],
  },
  {
    n: "04",
    icon: CreditCard,
    title: "Poznejte sázkové akce",
    desc: "V každém kole máte na výběr z několika akcí. Správná volba závisí na síle vaší ruky, pozici u stolu a chování soupeřů.",
    bullets: [
      "Check — zůstáváte ve hře bez sázky",
      "Call — dorovnáte sázku soupeře",
      "Raise — zvýšíte sázku",
      "Fold — vzdáváte toto kolo",
      "All-in — vsadíte všechny žetony",
    ],
  },
  {
    n: "05",
    icon: Gamepad2,
    title: "Vyberte si variantu pokeru",
    desc: "Texas Hold'em je ideální pro začátečníky. Omaha nabízí více akce s čtyřmi kartami v ruce. 7-Card Stud je klasika bez společných karet.",
    bullets: [
      "Texas Hold'em — 2 karty, nejpopulárnější, pro začátečníky",
      "Omaha — 4 karty, silnější kombinace, pro pokročilé",
      "7-Card Stud — 7 karet, žádné společné, vyžaduje paměť",
    ],
  },
  {
    n: "06",
    icon: Target,
    title: "Osvojte si základní strategii",
    desc: "Začněte u nízkých sázek a hrajte tight-aggressive styl — méně handů, ale každý agresivně. Sledujte soupeře a dodržujte bankroll management.",
    bullets: [
      "Začněte u nízkých sázek (mikrostaky)",
      "Hrajte tight-aggressive styl (TAG)",
      "Sledujte vzorce sázek soupeřů",
      "Max. 5–10 % bankrollu na jeden stůl",
    ],
  },
];

const FAQS = [
  { q: "Kolik karet dostane hráč v Texas Hold'em?", a: "Každý hráč dostane dvě zakryté karty (hole cards). K nim se postupně odkrývá pět společných karet na stole — flop (3), turn (1) a river (1)." },
  { q: "Co je silnější – straight nebo flush?", a: "Flush je silnější než straight. Pamatujte: barva (flush) poráží postupku (straight), ale prohrává s full housem." },
  { q: "Jak dlouho trvá jedna pokerová hra?", a: "Cash game ruce trvají jen pár minut, turnaje mohou trvat hodiny i celý den. Online poker bývá rychlejší než live díky automatickému dealování." },
  { q: "Mohu hrát poker zdarma online?", a: "Ano, mnoho kasin a aplikací nabízí free verze (play money) pro trénink. Je to ideální způsob, jak se naučit pravidla bez finančního rizika." },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eef1f7] last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left gap-4">
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#003da5] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 text-sm leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

export default function PokerJakHratPage() {
  useSEO({ title: "Jak hrát poker – pravidla Texas Hold'em pro začátečníky (2026) | HrajBonus", description: "Jak se hraje poker krok za krokem: klasická poker pravidla, průběh rozdání, sázkové akce a nejčastější chyby začátečníků. Texas Hold'em srozumitelně. 18+",
  url: "https://hrajbonus.com/poker/jak-hrat", });

  return (
    <div className="min-h-screen bg-[#f5f7fb]" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 text-white/60 text-sm font-semibold">
              <Link href="/poker" className="hover:text-white transition-colors">Poker</Link>
              <span>/</span>
              <span className="text-white/90">Jak hrát poker</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold" data-testid="badge-aktualizovano-jak-hrat">
              <Calendar className="w-3 h-3" /> Aktualizováno: 12. března 2026
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5">
            Jak hrát poker – kompletní návod pro začátečníky
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-6">
            Poker je karetní hra, která kombinuje strategii, matematiku i psychologii. Cílem je vytvořit co nejsilnější kombinaci pěti karet, nebo přesvědčit soupeře, aby své karty složili. Nejhranější variantou je Texas Hold'em, ale existují i další verze jako Omaha nebo 7-Card Stud.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#pravidla">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                <CheckCircle className="w-3.5 h-3.5" /> Základní pravidla
              </div>
            </a>
            <a href="#prubehu">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                <RefreshCw className="w-3.5 h-3.5" /> Průběh hry
              </div>
            </a>
            <a href="#varianty">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                <Layers className="w-3.5 h-3.5" /> Varianty pokeru
              </div>
            </a>
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      {/* Poker cross-navigation */}
      <nav className="bg-[#f5f7fb] border-b border-[#dde4ee] sticky top-16 z-20" data-testid="poker-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {[
              { href: "/poker", label: "Poker přehled", active: false },
              { href: "/poker/kombinace", label: "Kombinace", active: false },
              { href: "/poker/strategie", label: "Strategie", active: false },
              { href: "/poker/jak-hrat", label: "Jak hrát", active: true },
            ].map(({ href, label, active }) => (
              <Link key={href} href={href}>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-[#003da5] text-white"
                      : "text-gray-600 hover:bg-white hover:text-[#003da5]"
                  }`}
                  data-testid={`poker-nav-${href.replace(/\//g, "-")}`}
                >
                  {label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-8">

        {/* Co potřebujete vědět před první hrou */}
        <section data-testid="section-pred-prvni-hrou">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-3">Co potřebujete vědět před první hrou</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              Poker není čistě hra náhody. Na rozdíl od slotů nebo rulety má dlouhodobě dovednostní složku – ale to neznamená, že začátečník nic nevyhraje. Tady je minimum, které musíte ovládnout, než vsadíte první korunu.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: "01", title: "Cíl hry", desc: "Sestavit nejsilnější pětikaretní kombinaci nebo přimět všechny soupeře složit karty. Nevyhrává vždy ten s nejsilnější rukou – kdo zůstane poslední ve hře, bere pot." },
                { icon: "02", title: "Pořadí kombinací", desc: "Musíte znát 10 kombinací zpaměti – od High Card po Royal Flush. Nejrychlejší způsob: vytiskněte si tabulku kombinací a mějte ji po ruce při prvních hrách." },
                { icon: "03", title: "Sázkové kola", desc: "Texas Hold'em má 4 kola sázení: pre-flop, flop, turn a river. V každém kole rozhodujete: check, call, raise nebo fold. Chyby v sázkách stojí nejvíce." },
                { icon: "04", title: "Pozice u stolu", desc: "Kdo mluví jako poslední (button), má výhodu – vidí akce všech ostatních. Pozice je jeden z nejdůležitějších pojmů v pokeru, i pro začátečníky." },
              ].map(item => (
                <div key={item.icon} className="bg-[#f3f6fb] rounded-xl p-4 flex gap-3">
                  <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-black text-sm text-white" style={{ background: "#003da5" }}>{item.icon}</div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm mb-1">{item.title}</div>
                    <p className="text-gray-600 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 rounded-xl border border-[#003da5]/20 bg-[#f0f5ff]">
              <p className="text-[#003da5] text-sm font-semibold">
                Pořadí kombinací si prohlédněte podrobně na stránce{" "}
                <Link href="/poker/kombinace" className="underline hover:no-underline">Pokerové kombinace</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Numbered step layout — methodology style */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3">6 kroků k prvnímu pokeru</h2>
            <p className="text-gray-500 text-base mb-8">Kompletní průvodce od pravidel po strategii — krok za krokem.</p>

            <div className="space-y-6">
              {LEARNING_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.n}
                    id={`step-${step.n}`}
                    className="relative"
                    data-testid={`learning-step-${step.n}`}
                  >
                    {i < LEARNING_STEPS.length - 1 && (
                      <div className="absolute left-[23px] top-[56px] bottom-[-24px] w-0.5 bg-[#003da5]/15 z-0" />
                    )}
                    <div className="relative z-10 flex gap-5">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">
                            {step.n}
                          </span>
                          <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                        <ul className="space-y-2">
                          {step.bullets.map((bullet, bi) => (
                            <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Nejčastější chyby začátečníků */}
        <section data-testid="section-chyby-zacatecniku">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-3">Nejčastější chyby začátečníků v pokeru</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              I dobře naučená pravidla nestačí, pokud opakujete stejné chyby. Tady jsou nejrozšířenější problémy, se kterými se začátečníci setkávají, a jak se jim vyhnout.
            </p>
            <div className="space-y-3">
              {[
                { chyba: "Hrání příliš mnoha handů", reseni: "Disciplína ve výběru startovních karet je základ. Hrajte jen silné kombinace z dobré pozice – zejména zpočátku. Fold není prohra, je to ochrana bankrollu." },
                { chyba: "Ignorování pozice u stolu", reseni: "Hráč na tlačítku (button) mluví jako poslední a má velkou výhodu. Z rané pozice hrajte jen nejsilnější ruce, z pozdní si můžete dovolit více." },
                { chyba: "Chase drawing handů za každou cenu", reseni: "Pronásledování flush nebo straight za každou sázku je drahé. Naučte se základní pot odds – zda šance na dokreslení ospravedlňují zaplacení sázky." },
                { chyba: "Bluffování příliš často (nebo nikdy)", reseni: "Začátečníci buď nesmyslně blufují, nebo se blufování bojí. Bluff funguje, když dává příběh a cílí na hráče, kteří jsou schopni složit karty." },
                { chyba: "Tilt – hraní pod vlivem emocí", reseni: "Prohraná ruka vás nesmí rozhazovat do dalšího kola. Pokud cítíte frustraci, udělejte pauzu. Tilt je největší nepřítel dlouhodobě výdělečného pokeru." },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-xl bg-[#f3f6fb]">
                  <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-red-100 text-red-600 font-black text-xs">{i + 1}</div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm mb-1">{item.chyba}</div>
                    <p className="text-gray-600 text-xs leading-relaxed">{item.reseni}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Rozdíl mezi cash game a turnajem */}
        <section data-testid="section-cash-vs-turnaj">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-3">Cash game nebo turnaj? Klíčový rozdíl pro začátečníky</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              Online poker nabízí dva základní formáty hry. Výběr správného formátu je pro začátečníka stejně důležitý jako znalost pravidel.
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="bg-[#f0f5ff] border border-[#003da5]/15 rounded-xl p-5">
                <div className="font-bold text-[#003da5] text-base mb-2">Cash Game (Ring Game)</div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" /><span>Můžete nastoupit a odejít kdykoli</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" /><span>Žetony mají reálnou hodnotu (1 Kč = 1 Kč)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" /><span>Blindy jsou pevné – tempo hry se nemění</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" /><span>Ideální pro konzistentní výdělky</span></li>
                </ul>
                <div className="mt-3 text-xs text-[#003da5] font-semibold">Doporučeno pro: hráče s omezeným časem a stabilní strategií</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="font-bold text-amber-800 text-base mb-2">Turnaj (MTT / SNG)</div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><span>Pevný buy-in, fixní maximální ztráta</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><span>Blindy se postupně zvyšují – nutí k akci</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><span>Velké výhry při hlubokém postupu (Top 10 %)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><span>Trvá hodiny i celý den</span></li>
                </ul>
                <div className="mt-3 text-xs text-amber-700 font-semibold">Doporučeno pro: hráče, kteří chtějí vzrušení a velké poty s omezeným rizikem</div>
              </div>
            </div>
            <div className="mt-4 p-4 rounded-xl bg-[#f3f6fb] text-sm text-gray-600 leading-relaxed">
              <strong className="text-gray-900">Tip pro začátečníky:</strong> Začněte freerolly (bezplatné turnaje) nebo low-stakes cash game s buy-inem pod 20 Kč. Cílem prvních her je porozumět dynamice stolu, ne vydělávat.
            </div>
          </div>
        </section>

        {/* Next steps */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Co dál? Prohloubte své znalosti</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              Tato stránka vám dala základ. Pokud chcete hrát lépe a vyhrávat konzistentněji, doporučujeme projít naše podrobnější průvodce:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link href="/poker/kombinace">
                <div className="group flex items-center justify-between p-4 border border-[#003da5]/20 bg-[#f0f5ff] rounded-xl hover:border-[#003da5] hover:bg-[#e8f0fe] transition-colors cursor-pointer">
                  <div>
                    <div className="font-bold text-[#003da5] text-sm mb-1">Základy pokerové kombinace</div>
                    <p className="text-gray-500 text-xs">Naučte se pořadí kombinací, kickery a jak skenovat board jako profík.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#003da5] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              <Link href="/poker/strategie">
                <div className="group flex items-center justify-between p-4 border border-[#003da5]/20 bg-[#f0f5ff] rounded-xl hover:border-[#003da5] hover:bg-[#e8f0fe] transition-colors cursor-pointer">
                  <div>
                    <div className="font-bold text-[#003da5] text-sm mb-1">Pokerové strategie</div>
                    <p className="text-gray-500 text-xs">TAG styl, bluffing, range thinking, bankroll management a EV.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#003da5] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-5">Časté dotazy</h2>
            <div>
              {FAQS.map(item => <FaqItem key={item.q} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        {/* Responsible gaming */}
        <div className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm flex items-start gap-4">
          <Shield className="w-5 h-5 text-[#003da5] mt-0.5 flex-shrink-0" />
          <p className="text-gray-600 text-sm leading-relaxed">
            <span className="font-bold text-gray-900">Pouze pro hráče 18+. </span>
            Hrajte zodpovědně. Nastavte si limity a sázejte jen tolik, kolik si můžete dovolit prohrát. Pokud potřebujete pomoc, obraťte se na{" "}
            <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gamcare-poker-jak-hrat">GamCare</a>.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium pb-4">
          <Shield className="w-3.5 h-3.5" />
          <span>Pouze pro hráče 18+. Hrajte zodpovědně. Podmínky se vztahují. |{" "}
            <a href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-gambleaware-poker-jak-hrat">GambleAware</a>
          </span>
        </div>

      </div>
      <Footer />
    </div>
  );
}
